<template>
  <div class="modal fade show d-block" tabindex="-1" style="background-color: rgba(0,0,0,0.5)">
    <div class="modal-dialog modal-lg modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Session Details</h5>
          <button type="button" class="btn-close" @click="$emit('close')"></button>
        </div>
        <div class="modal-body">
          <div v-if="session">
            <!-- Session Overview -->
            <div class="row mb-4">
              <div class="col-md-6">
                <div class="card bg-light">
                  <div class="card-body">
                    <h6 class="text-muted mb-1">Session Information</h6>
                    <p class="mb-1"><strong>Date:</strong> {{ formatDate(session.session_date) }}</p>
                    <p class="mb-1"><strong>Time:</strong> {{ formatTime(session.session_date) }}</p>
                    <p class="mb-1"><strong>Type:</strong>
                      <span :class="'badge ' + getTypeBadge(session.session_type)">
                        {{ session.session_type }}
                      </span>
                    </p>
                    <p class="mb-1"><strong>Venue:</strong> {{ session.venue }}</p>
                    <p class="mb-0"><strong>Duration:</strong> {{ session.duration_minutes }} minutes</p>
                  </div>
                </div>
              </div>
              <div class="col-md-6">
                <div class="card bg-light">
                  <div class="card-body">
                    <h6 class="text-muted mb-1">Topic Details</h6>
                    <p class="mb-1"><strong>Topic:</strong> {{ session.topic }}</p>
                    <p class="mb-1"><strong>Category:</strong>
                      <span :class="'badge ' + getCategoryBadge(session.topic_category)">
                        {{ formatCategory(session.topic_category) }}
                      </span>
                    </p>
                    <p class="mb-0"><strong>Target Audience:</strong> {{ session.target_audience || 'General' }}</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Attendance Information -->
            <div class="card mb-3">
              <div class="card-header bg-white">
                <h6 class="mb-0">Attendance Information</h6>
              </div>
              <div class="card-body">
                <div class="row">
                  <div class="col-md-4 text-center">
                    <h3 class="text-primary mb-1">{{ session.attendees_count }}</h3>
                    <p class="text-muted mb-0">Total Attendees</p>
                  </div>
                  <div class="col-md-4 text-center">
                    <h3 class="text-info mb-1">{{ session.male_attendees }}</h3>
                    <p class="text-muted mb-0">Male Attendees</p>
                  </div>
                  <div class="col-md-4 text-center">
                    <h3 class="text-danger mb-1">{{ session.female_attendees }}</h3>
                    <p class="text-muted mb-0">Female Attendees</p>
                  </div>
                </div>
                <div v-if="session.age_group" class="mt-3">
                  <p class="mb-1"><strong>Age Group:</strong> {{ session.age_group }}</p>
                </div>
              </div>
            </div>

            <!-- Household Information (if individual session) -->
            <div v-if="session.household_id" class="card mb-3">
              <div class="card-header bg-white">
                <h6 class="mb-0">Household Information</h6>
              </div>
              <div class="card-body">
                <p class="mb-1"><strong>Household Number:</strong> {{ session.household_number }}</p>
                <p class="mb-0"><strong>Household ID:</strong> {{ session.household_id }}</p>
              </div>
            </div>

            <!-- Key Messages -->
            <div v-if="session.key_messages" class="card mb-3">
              <div class="card-header bg-white">
                <h6 class="mb-0">Key Messages Delivered</h6>
              </div>
              <div class="card-body">
                <ul class="mb-0">
                  <li v-for="(message, index) in getKeyMessages(session.key_messages)" :key="index">
                    {{ message }}
                  </li>
                </ul>
              </div>
            </div>

            <!-- Materials and Methods -->
            <div class="row mb-3">
              <div class="col-md-6">
                <div class="card h-100">
                  <div class="card-header bg-white">
                    <h6 class="mb-0">Educational Materials</h6>
                  </div>
                  <div class="card-body">
                    <p v-if="session.materials_used" class="mb-0">{{ session.materials_used }}</p>
                    <p v-else class="text-muted mb-0">No materials used</p>
                  </div>
                </div>
              </div>
              <div class="col-md-6">
                <div class="card h-100">
                  <div class="card-header bg-white">
                    <h6 class="mb-0">Demonstration</h6>
                  </div>
                  <div class="card-body">
                    <p v-if="session.demonstration_done" class="mb-0 text-success">
                      <i class="bx bx-check-circle"></i> Demonstration conducted
                    </p>
                    <p v-else class="text-muted mb-0">No demonstration</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Additional Information -->
            <div v-if="session.challenges_faced || session.behavior_change_observed || session.notes" class="card mb-3">
              <div class="card-header bg-white">
                <h6 class="mb-0">Additional Information</h6>
              </div>
              <div class="card-body">
                <div v-if="session.challenges_faced" class="mb-2">
                  <strong>Challenges Faced:</strong>
                  <p class="mb-0">{{ session.challenges_faced }}</p>
                </div>
                <div v-if="session.behavior_change_observed" class="mb-2">
                  <strong>Behavior Change Observed:</strong>
                  <p class="mb-0">{{ session.behavior_change_observed }}</p>
                </div>
                <div v-if="session.notes" class="mb-0">
                  <strong>Notes:</strong>
                  <p class="mb-0">{{ session.notes }}</p>
                </div>
              </div>
            </div>

            <!-- Tests Conducted -->
            <div v-if="hasTests" class="card mb-3">
              <div class="card-header bg-white">
                <h6 class="mb-0">Tests Conducted</h6>
              </div>
              <div class="card-body">
                <div class="row">
                  <div class="col-md-4">
                    <p class="mb-1">
                      <strong>Pre-test:</strong>
                      <span v-if="session.pre_test_done" class="text-success">
                        <i class="bx bx-check"></i> Done
                      </span>
                      <span v-else class="text-muted">Not done</span>
                    </p>
                  </div>
                  <div class="col-md-4">
                    <p class="mb-1">
                      <strong>Post-test:</strong>
                      <span v-if="session.post_test_done" class="text-success">
                        <i class="bx bx-check"></i> Done
                      </span>
                      <span v-else class="text-muted">Not done</span>
                    </p>
                  </div>
                  <div class="col-md-4">
                    <p class="mb-0">
                      <strong>Follow-up Planned:</strong>
                      <span v-if="session.follow_up_planned" class="text-success">
                        <i class="bx bx-check"></i> Yes
                      </span>
                      <span v-else class="text-muted">No</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Metadata -->
            <div class="text-muted small">
              <p class="mb-1">
                <strong>Conducted by:</strong> {{ session.conducted_by_name || 'Unknown' }}
              </p>
              <p class="mb-0">
                <strong>Recorded on:</strong> {{ formatDateTime(session.created_at) }}
              </p>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="$emit('close')">Close</button>
          <button type="button" class="btn btn-primary" @click="printSession">
            <i class="bx bx-printer"></i> Print
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "SessionDetailsModal",
  props: {
    session: {
      type: Object,
      required: true
    }
  },
  computed: {
    hasTests() {
      return this.session.pre_test_done || this.session.post_test_done || this.session.follow_up_planned;
    }
  },
  methods: {
    getKeyMessages(messages) {
      if (!messages) return [];
      return messages.split(';').map(m => m.trim()).filter(m => m);
    },

    getTypeBadge(type) {
      const badges = {
        'individual': 'bg-primary',
        'group': 'bg-success',
        'community': 'bg-warning'
      };
      return badges[type] || 'bg-secondary';
    },

    getCategoryBadge(category) {
      const badges = {
        'maternal_health': 'badge-soft-danger',
        'child_health': 'badge-soft-primary',
        'nutrition': 'badge-soft-success',
        'hygiene_sanitation': 'badge-soft-info',
        'disease_prevention': 'badge-soft-warning',
        'family_planning': 'badge-soft-secondary'
      };
      return badges[category] || 'badge-soft-secondary';
    },

    formatCategory(category) {
      const labels = {
        'maternal_health': 'Maternal Health',
        'child_health': 'Child Health',
        'nutrition': 'Nutrition',
        'hygiene_sanitation': 'Hygiene & Sanitation',
        'disease_prevention': 'Disease Prevention',
        'family_planning': 'Family Planning'
      };
      return labels[category] || category;
    },

    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },

    formatTime(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    formatDateTime(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    printSession() {
      window.print();
    }
  }
};
</script>

<style scoped>
.modal {
  display: block;
}

.badge-soft-danger {
  color: #f46a6a;
  background-color: rgba(244, 106, 106, 0.18);
}

.badge-soft-warning {
  color: #f1b44c;
  background-color: rgba(241, 180, 76, 0.18);
}

.badge-soft-primary {
  color: #556ee6;
  background-color: rgba(85, 110, 230, 0.18);
}

.badge-soft-success {
  color: #50a5f1;
  background-color: rgba(80, 165, 241, 0.18);
}

.badge-soft-info {
  color: #50a5f1;
  background-color: rgba(80, 165, 241, 0.18);
}

.badge-soft-secondary {
  color: #74788d;
  background-color: rgba(116, 120, 141, 0.18);
}
</style>